import {
  Dialog, DialogContent, DialogTitle, IconButton, Tooltip,
  Typography, Box,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';
import { Asset, CertificationCategory } from '@x-guard/xgac-types/xgac';
import { useEffect, useState } from 'react';
import {
  Button,
  FieldProps, ReferenceField, useRecordContext, useTranslate,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import type { CertificationDocument } from '../../../utils/certificationDocuments';

type AssetCertification = NonNullable<Asset['certifications']>[number];
type DocumentaryProofInputValue = {
  rawFile?: File;
};

export const DocumentaryProofPreview = (props: {
  documentaryProof: string;
  fileName?: string;
  mimeType?: string;
  imageStyle?: React.CSSProperties;
  pdfStyle?: React.CSSProperties;
}) => {

  const isImage = props.mimeType?.startsWith('image/')
    || props.fileName?.match(/\.(jpg|jpeg|png)$/i)
    || props.documentaryProof.match(/\.(jpg|jpeg|png)(\?.*)?$/i);

  if (isImage) {

    return (
      <img
        src={props.documentaryProof}
        alt="Documentary proof"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          ...props.imageStyle,
        }}
      />
    );

  }

  return (
    <iframe
      src={props.documentaryProof}
      title="Documentary proof"
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        ...props.pdfStyle,
      }}
    />
  );

};

export const DocumentaryProofInputPreview = (props: {
  source: string;
  existingDocumentaryProof?: string | null;
  imageStyle?: React.CSSProperties;
  pdfStyle?: React.CSSProperties;
}) => {

  const documentaryProofInputValue = useWatch({ name: props.source }) as DocumentaryProofInputValue | undefined;
  const rawFile = documentaryProofInputValue?.rawFile;
  const [objectUrl, setObjectUrl] = useState<string>();

  useEffect(() => {

    if (!rawFile) {

      setObjectUrl(undefined);
      return undefined;

    }

    const nextObjectUrl = URL.createObjectURL(rawFile);
    setObjectUrl(nextObjectUrl);

    return () => URL.revokeObjectURL(nextObjectUrl);

  }, [rawFile]);

  if (objectUrl) {

    return (
      <DocumentaryProofPreview
        documentaryProof={objectUrl}
        fileName={rawFile?.name}
        mimeType={rawFile?.type}
        imageStyle={props.imageStyle}
        pdfStyle={props.pdfStyle}
      />
    );

  }

  if (props.existingDocumentaryProof) {

    return (
      <DocumentaryProofPreview
        documentaryProof={props.existingDocumentaryProof}
        imageStyle={props.imageStyle}
        pdfStyle={props.pdfStyle}
      />
    );

  }

  return null;

};

export const CertificationDocumentPreviewButton = (props: {
  source: string;
}) => {

  const translate = useTranslate();
  const document = useWatch({ name: props.source }) as CertificationDocument | undefined;

  const [open, setOpen] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string>();

  useEffect(() => {

    if (!document?.rawFile) {

      setObjectUrl(undefined);
      return undefined;

    }

    const url = URL.createObjectURL(document.rawFile);
    setObjectUrl(url);

    return () => URL.revokeObjectURL(url);

  }, [document?.rawFile]);

  const previewUrl = objectUrl ?? document?.src;

  if (!previewUrl) return null;

  const fileName = document?.title ?? document?.rawFile?.name;
  const mimeType = document?.rawFile?.type;

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          width: 48,
          height: 64,
          boxSizing: 'border-box',
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          borderRadius: 0.5,
          bgcolor: 'common.white',
        }}
      >
        <DocumentaryProofPreview
          documentaryProof={previewUrl}
          fileName={fileName}
          mimeType={mimeType}
          imageStyle={{
            display: 'block',
            width: '100%',
            height: 'auto',
            objectFit: 'contain',
            pointerEvents: 'none',
          }}
          pdfStyle={{
            width: 320,
            height: 426,
            transform: 'scale(0.15)',
            transformOrigin: 'top left',
            pointerEvents: 'none',
          }}
        />
        <Tooltip
          title={translate(
            'resources.assets.certifications.text.preview_file',
          )}
        >
          <IconButton
            size="small"
            onClick={() => setOpen(true)}
            aria-label={translate(
              'resources.assets.certifications.text.preview_file',
            )}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              borderRadius: 0.5,
              color: 'common.white',
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.18)',
              },
            }}
          >
            <ZoomInIcon
              sx={{
                fontSize: 16,
                boxSizing: 'content-box',
                p: 0.5,
                borderRadius: '50%',
                bgcolor: 'rgba(0, 0, 0, 0.55)',
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '90vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {fileName ?? translate('resources.assets.certifications.fields.documentary_proof')}
          <IconButton
            aria-label={translate('general.text.close')}
            onClick={() => setOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: '70vh' }}>
          <DocumentaryProofPreview
            documentaryProof={previewUrl}
            fileName={fileName}
            mimeType={document?.rawFile?.type}
            imageStyle={{ flex: 1, minHeight: 0 }}
            pdfStyle={{ flex: 1, minHeight: '70vh' }}
          />
        </DialogContent>
      </Dialog>
    </>
  );

};

const DocumentaryProofPreviewButton = (props: { documentaryProof: string }) => {

  const [open, setOpen] = useState(false);
  const translate = useTranslate();

  return (
    <>
      <Button
        startIcon={<AttachFileIcon/>}
        onClick={() => setOpen(true)}
        variant='contained'
        label='resources.assets.certifications.text.view_document'
        size="small"
        sx={{
          whiteSpace: 'nowrap',
        }}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '90vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle className="flex-in-between">
          <span>Documentary proof</span>
          <IconButton aria-label={translate('general.text.close')} onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
          <DocumentaryProofPreview
            documentaryProof={props.documentaryProof}
            imageStyle={{
              flex: 1,
              minHeight: 0,
            }}
            pdfStyle={{
              flex: 1,
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );

};

const DocumentaryProofValue = (props: { documentaryProof?: string | null }) => {

  const translate = useTranslate();
  const categoryRecord = useRecordContext<CertificationCategory>();

  if (!categoryRecord) {

    return null;

  }

  let proofContent: React.ReactNode;

  if (categoryRecord.isDocumentaryProofRequired) {

    proofContent = props.documentaryProof ? (
      <DocumentaryProofPreviewButton documentaryProof={props.documentaryProof} />
    ) : (

      <Chip label={translate('resources.assets.certifications.text.not_provided')} color="error" />

    );

  } else {

    proofContent = (
      <Tooltip
        title={translate('resources.assets.certifications.text.not_required_description')}
        placement="bottom"
        arrow
      >
        <Typography variant="caption" color="text.secondary" component="span">
          {translate('resources.assets.certifications.text.not_required')}
        </Typography>
      </Tooltip>
    );

  }

  return (
    <>
      {proofContent}
    </>
  );

};

export const DocumentaryProofField = (props: FieldProps) => {

  const translate = useTranslate();
  const certificationRecord = useRecordContext<AssetCertification>();
  if (!certificationRecord) {

    return null;

  }

  return (
    <ReferenceField
      {...props}
      source="certificationCategory._id"
      reference="certificates"
      label={translate('resources.assets.certifications.fields.documentary_proof')}
      link={false}
    >
      <DocumentaryProofValue documentaryProof={certificationRecord.documentaryProof} />
    </ReferenceField>
  );

};
